import { useState } from 'react';

import { OrderMethod } from '@/domain/models/Order/orderMethod';
import { errorHandler } from '@/lib/ErrorHandler';
import { getClient } from '@/lib/restClient';
import { SalesPeriodType } from '@/pages/menu-managements/items/hooks/useMenuManagementItemForm';
import {
  getSearchItems,
  SortObject,
} from '@/pages/menu-managements/services/searchItem';
import { Title } from '@/pages/menu-managements/services/title';
import {
  BrandItemDelivery,
  BrandItemEatIn,
  BrandItemTakeout,
} from '@/services/BrandItem';
import { PreviewBrandItem } from '@/services/BrandItem/hooks/upload/useMenuManagementUberPreviewBulkUpsertBrandItems';

import { MenuManagementBrandItemService } from '../../MenuManagementItemService';

type Parameters =
  | { associateBrandId: string; file: File }
  | {
      associateBrandId: number;
      brandItems: BulkUpsertBrandItem[];
      brandCategories: BulkUpsertBrandCategory[];
      brandOptionGroups: BulkUpsertBrandOptionGroup[];
      uberCategoryIdsByUberMenuItemId: {
        [uber_menu_item_id: string]: string[];
      };
      uberModifierGroupIdsByUberMenuItemId: {
        [uber_menu_item_id: string]: string[];
      };
    };

export type BulkUpsertBrandItem = {
  title: Title;
  image_url: string | null;
  memo: string | null;
  description: string | null;
  sales_start_at: string | null;
  sales_end_at: string | null;
  takeout: BrandItemTakeout | null;
  delivery: BrandItemDelivery | null;
  eat_in: BrandItemEatIn | null;
  order_methods: OrderMethod[];
  can_serve_alone: boolean;
  brand_option_group_sort_objects: SortObject[];
  is_auth_required: boolean;
  sales_period_type: SalesPeriodType;
  limit_of_purchase: number | null;
  relative_sales_start_at: number | null;
  relative_sales_end_at: number | null;
  uber_menu_item_id: string | null;
};

export type BulkUpsertBrandCategory = {
  id: string;
  title: Title;
  uber_category_id: string | null;
};
export type BulkUpsertBrandOptionGroup = {
  id: string;
  title: Title;
  quantity_min: number;
  qauntity_max: number | null;
  uber_modifier_group_id: string | null;
  uber_modifier_option_item_ids: string[] | null;
};

export const fromPreviewBrandItem = (
  brandItem: PreviewBrandItem
): BulkUpsertBrandItem => {
  return {
    ...brandItem,
    brand_option_group_sort_objects: getSearchItems(
      brandItem.brand_option_group_sort_objects
    ),
  };
};

const useMenuManagementBulkUpsertBrandItems = <T extends Parameters>(): {
  loading: boolean;
  upload: (params: T) => Promise<void>;
} => {
  const [loading, setLoading] = useState(false);
  const upload = async (params: Parameters): Promise<void> => {
    try {
      setLoading(true);
      const menuManagementBrandItemService = new MenuManagementBrandItemService(
        getClient()
      );

      if ('brandItems' in params) {
        await menuManagementBrandItemService.upsertBrandItems(params);
      } else {
        // TODO: csvアップロードの場合も↑のAPI使えそう
        await menuManagementBrandItemService.uploadCsvFile(params);
      }
    } catch (error) {
      errorHandler({ error: error as Error });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    upload,
  };
};

export default useMenuManagementBulkUpsertBrandItems;
